import { useEffect, useRef } from 'react';
import { Map, AdvancedMarker, useMap } from '@vis.gl/react-google-maps';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { PolygonLayer } from '@deck.gl/layers';

/**
 * GoldDotMarker — Custom DOM glyph used inside <AdvancedMarker> as children.
 * We avoid <Pin> from the lib because its internal PinModern forwards ALL props
 * (including platform-injected ones like `x-file-name` from Emergent's source
 * tracker) to google.maps.marker.PinElement which strictly validates property
 * names and throws on unknowns, crashing the whole map.
 */
const GoldDotMarker = ({ scale = 1, color = '#D9A05B', border = '#0E0E10' }) => (
  <div
    style={{
      width: 26 * scale,
      height: 26 * scale,
      borderRadius: '50%',
      background: color,
      border: `2px solid ${border}`,
      boxShadow: `0 0 ${12 * scale}px ${3 * scale}px rgba(217,160,91,0.55)`,
      transform: 'translateY(0)',
      cursor: 'pointer',
    }}
  />
);

/**
 * VectorMap3D — Sprint Layers (Feb 2026) / Layer 1.
 *
 * Carte Google Vector avec tilt 67.5° et bâtiments 3D vectoriels.
 *
 * Pré-requis :
 *  - REACT_APP_GOOGLE_MAPS_API_KEY (nouveau nom canonique, voir lib/mapsKey.js)
 *  - REACT_APP_GOOGLE_VECTOR_MAP_ID (créé via GCP Map Management
 *    avec Tilt activé)
 *
 * Différences avec Map3D (Photorealistic) :
 *  - VRAM ~10x moins lourde → cohabite avec d'autres contextes WebGL
 *  - Building data : vector LOD shapes (gris stylisés) au lieu de mesh photo
 *  - Pas de flyCameraAround : on simule via `panTo` + tilt/heading lerps
 *
 * Props identiques à Hub3DMap (sous-ensemble) pour permettre la bascule
 * sans changer l'API publique du parent.
 */

const VectorMap3D = ({
  apiKey,
  mapId,
  projects = [],
  selectedId = null,
  onMarkerClick,
  defaultCenter = { lat: 46.6, lng: 2.45 },
  fitAllNonce = 0,
  flyTo = null,
  dropPin = null,
  onMapClick,
  cadastralPolygon = null,  // Phase 2 — [[lat, lon], ...] OSM building footprint
  cadastralHeight = null,   // Phase 2 — meters (when inspection mode is on)
  inspectionMode = false,   // Phase 2 — extrude cadastral polygon as a 3D gold box
  cadastreParcels = null,   // Phase 3.A — list of {parcelle_id, polygon, ...}
  selectedParcelleId = null, // Phase 3.A — highlighted parcel id
  onParcelClick = null,     // Phase 3.A — clicking a parcel triggers selection
  planFootprints = null,    // Sprint 3.B P2 — list of {project_id, name, polygon_latlon, ...}
  onFootprintClick = null,  // Sprint 3.B P2 — clicking a footprint triggers project selection
}) => {
  const pinned = projects.filter((p) => Array.isArray(p.coords) && p.coords.length === 2);
  const selected = pinned.find((p) => p.project_id === selectedId);

  return (
    <div className="w-full h-full" data-testid="vector-map-3d">
      <Map
          mapId={mapId || undefined}
          defaultCenter={defaultCenter}
          defaultZoom={5.2}
          defaultTilt={mapId ? 67.5 : 0}  // tilt only meaningful with vector mapId
          defaultHeading={0}
          gestureHandling="greedy"
          disableDefaultUI={false}
          mapTypeControl={false}
          fullscreenControl={false}
          streetViewControl={false}
          rotateControl
          style={{ width: '100%', height: '100%', background: '#0a0a0d' }}
          data-testid="vector-map-canvas"
          onClick={(e) => {
            const latLng = e?.detail?.latLng;
            if (latLng && typeof latLng.lat === 'number') {
              onMapClick?.(latLng.lat, latLng.lng);
            }
          }}
        >
          <FitAllVector projects={pinned} nonce={fitAllNonce} />
          <FlyToSelectedVector selectedProject={selected} />
          <FlyToAddressVector target={flyTo} />
          <FlyToDropPinVector pin={dropPin} />
          <CadastreParcelsOverlay
            parcels={cadastreParcels}
            selectedParcelleId={selectedParcelleId}
            onParcelClick={onParcelClick}
          />
          <PlanFootprintsOverlay
            footprints={planFootprints}
            selectedProjectId={selectedId}
            onFootprintClick={onFootprintClick}
          />
          <CadastralOverlay
            polygon={cadastralPolygon}
            heightM={cadastralHeight}
            extruded={inspectionMode}
          />

          {pinned.map((p) => (
            <AdvancedMarker
              key={p.project_id}
              position={{ lat: p.coords[0], lng: p.coords[1] }}
              onClick={() => onMarkerClick?.(p)}
            >
              <GoldDotMarker scale={selected?.project_id === p.project_id ? 1.3 : 1.0} />
            </AdvancedMarker>
          ))}

          {dropPin && (
            <AdvancedMarker
              key="drop"
              position={{ lat: dropPin.lat, lng: dropPin.lon }}
            >
              <GoldDotMarker scale={1.15} color="#2DD4BF" />
            </AdvancedMarker>
          )}
        </Map>
    </div>
  );
};

/* ---------------- Camera helpers ---------------- */

const FitAllVector = ({ projects, nonce = 0 }) => {
  const map = useMap();
  const lastNonce = useRef(undefined);
  useEffect(() => {
    if (!map || lastNonce.current === nonce) return;
    lastNonce.current = nonce;
    if (!projects.length) return;
    const bounds = new window.google.maps.LatLngBounds();
    projects.forEach((p) => bounds.extend({ lat: p.coords[0], lng: p.coords[1] }));
    map.fitBounds(bounds, 80);
  }, [map, projects, nonce]);
  return null;
};

const FlyToSelectedVector = ({ selectedProject }) => {
  const map = useMap();
  const last = useRef(null);
  useEffect(() => {
    if (!map || !selectedProject) return;
    const c = selectedProject.coords;
    if (!Array.isArray(c) || c.length < 2) return;
    const key = selectedProject.project_id;
    if (last.current === key) return;
    last.current = key;
    map.panTo({ lat: c[0], lng: c[1] });
    setTimeout(() => {
      try {
        map.setZoom(17);
        map.setTilt(67.5);
        map.setHeading((selectedProject._heading_seed || 0) % 360);
      } catch { /* setTilt only works on vector maps */ }
    }, 350);
  }, [map, selectedProject]);
  return null;
};

const FlyToAddressVector = ({ target }) => {
  const map = useMap();
  const lastKey = useRef(null);
  useEffect(() => {
    if (!map || !target || !target.lat || !target.lon) return;
    const key = `${target.lat}|${target.lon}|${target.nonce || ''}`;
    if (lastKey.current === key) return;
    lastKey.current = key;
    if (target.bbox && target.bbox.length === 4) {
      const [s, n, w, e] = target.bbox;
      map.fitBounds({ south: s, north: n, west: w, east: e }, 60);
    } else {
      map.panTo({ lat: target.lat, lng: target.lon });
      map.setZoom(15);
    }
  }, [map, target]);
  return null;
};

const FlyToDropPinVector = ({ pin }) => {
  const map = useMap();
  const lastKey = useRef(null);
  useEffect(() => {
    if (!map || !pin) return;
    const key = `${pin.lat}|${pin.lon}`;
    if (lastKey.current === key) return;
    lastKey.current = key;
    map.panTo({ lat: pin.lat, lng: pin.lon });
    if (map.getZoom() < 14) map.setZoom(14);
  }, [map, pin]);
  return null;
};

/* ---------------- Phase 2 — Deck.gl overlay (cadastral footprint) ---------------- */

const CadastralOverlay = ({ polygon, heightM = null, extruded = false }) => {
  const map = useMap();
  const overlayRef = useRef(null);
  const lastKeyRef = useRef(null);

  // Mount the GoogleMapsOverlay ONLY once the map has emitted its first
  // `idle` event. Before that, the underlying google.maps.Map instance
  // may be partially hydrated and Deck.gl's _onAdd throws on `addListener`.
  useEffect(() => {
    if (!map) return undefined;
    let cancelled = false;
    let cleanup = () => {};

    const attach = () => {
      if (cancelled) return;
      const overlay = new GoogleMapsOverlay({});
      overlay.setMap(map);
      overlayRef.current = overlay;
      cleanup = () => {
        overlay.setMap(null);
        overlayRef.current = null;
      };
    };

    if (window.google?.maps?.event) {
      // `idle` fires once the initial bounds/projection are ready.
      const listener = window.google.maps.event.addListenerOnce(map, 'idle', attach);
      return () => {
        cancelled = true;
        try { window.google.maps.event.removeListener(listener); } catch {/* noop */}
        cleanup();
      };
    }
    // Fallback: small timeout if google.maps.event isn't loaded yet
    const t = setTimeout(attach, 350);
    return () => { cancelled = true; clearTimeout(t); cleanup(); };
  }, [map]);

  // Refresh layers whenever the polygon changes + auto-fit the map
  // around the footprint so the gold overlay is always clearly visible.
  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    if (!polygon || polygon.length < 3) {
      overlay.setProps({ layers: [] });
      lastKeyRef.current = null;
      return;
    }
    // Deck.gl expects [lng, lat] pairs
    const contour = polygon.map(([lat, lon]) => [lon, lat]);
    // Effective extrusion height : explicit OSM `height_m`, fallback to a
    // conservative 12 m default (≈ 4 floors) so the inspection volume is
    // always visible even when OSM didn't tag the building.
    const elevation = extruded ? Math.max(8, heightM || 12) : 0;
    const layer = new PolygonLayer({
      id: 'cadastral-footprint',
      data: [{ contour, elevation }],
      getPolygon: (d) => d.contour,
      // 3D mode is more transparent (so the building underneath stays
      // readable), 2D footprint is opaque enough to read at a glance.
      getFillColor: extruded ? [217, 160, 91, 95] : [217, 160, 91, 130],
      getLineColor: extruded ? [255, 215, 130, 255] : [217, 160, 91, 240],
      getLineWidth: extruded ? 3 : 2,
      lineWidthMinPixels: extruded ? 3 : 2,
      stroked: true,
      filled: true,
      extruded,
      getElevation: (d) => d.elevation,
      wireframe: extruded,
      material: extruded ? { ambient: 0.55, diffuse: 0.85, shininess: 32, specularColor: [255, 230, 170] } : undefined,
      pickable: false,
      updateTriggers: {
        getFillColor: [extruded],
        getLineColor: [extruded],
        getLineWidth: [extruded],
        getElevation: [elevation],
      },
    });
    overlay.setProps({ layers: [layer] });

    // Fit camera to the polygon once per new footprint (dedup by first point)
    const key = `${polygon[0][0]}|${polygon[0][1]}|${polygon.length}`;
    if (lastKeyRef.current !== key && map && window.google?.maps?.LatLngBounds) {
      lastKeyRef.current = key;
      const bounds = new window.google.maps.LatLngBounds();
      polygon.forEach(([lat, lon]) => bounds.extend({ lat, lng: lon }));
      try {
        map.fitBounds(bounds, 140);
        // Bias the zoom a bit further to keep context (max 19 to avoid extreme close-up)
        setTimeout(() => {
          try { if (map.getZoom() > 19) map.setZoom(19); } catch {/* noop */}
        }, 400);
      } catch {/* noop — Vector Map only */}
    }
    // When inspection mode is active, force a strong tilt so the extruded
    // box is unmistakably 3D. Vector maps support up to 67.5°; raster
    // fallback only supports 0/45°. We try 67.5° first and let Google clamp.
    // Use `idle` so the tilt is applied AFTER any current animation
    // (fitBounds, panTo…) — calling setTilt mid-animation is silently dropped.
    if (extruded && map && window.google?.maps?.event) {
      const applyTilt = () => {
        try {
          const renderingType = map.getRenderingType?.();
          // 67.5° is vector-only ; 45° is the raster max
          map.setTilt(renderingType === 'VECTOR' ? 67.5 : 45);
          map.setHeading(((map.getHeading() || 0) + 35) % 360);
          if (renderingType !== 'VECTOR') {
            // Surface a one-time hint so devs/users understand why the 3D
            // effect is muted when the Vector Map fails to load (headless
            // browsers, restricted referrers, GPU-blocklisted contexts…).
             
            console.info('[CadastralOverlay] Vector Map unavailable — extrusion rendered at raster max tilt (45°).');
          }
        } catch {/* noop */}
      };
      const listener = window.google.maps.event.addListenerOnce(map, 'idle', applyTilt);
      const t = setTimeout(() => {
        try { if ((map.getTilt() || 0) < 30) applyTilt(); } catch {/* noop */}
      }, 700);
      return () => {
        clearTimeout(t);
        try { window.google.maps.event.removeListener(listener); } catch {/* noop */}
      };
    }
  }, [polygon, map, extruded, heightM]);

  return null;
};

// Marker click listener helper (used internally by AdvancedMarker)

/* ---------------- Phase 3.A — Deck.gl cadastre parcels overlay ---------------- */

const CadastreParcelsOverlay = ({ parcels, selectedParcelleId, onParcelClick }) => {
  const map = useMap();
  const overlayRef = useRef(null);

  useEffect(() => {
    if (!map) return undefined;
    let cancelled = false;
    let cleanup = () => {};
    const attach = () => {
      if (cancelled) return;
      const overlay = new GoogleMapsOverlay({});
      overlay.setMap(map);
      overlayRef.current = overlay;
      cleanup = () => { overlay.setMap(null); overlayRef.current = null; };
    };
    if (window.google?.maps?.event) {
      const listener = window.google.maps.event.addListenerOnce(map, 'idle', attach);
      return () => {
        cancelled = true;
        try { window.google.maps.event.removeListener(listener); } catch {/* noop */}
        cleanup();
      };
    }
    const t = setTimeout(attach, 350);
    return () => { cancelled = true; clearTimeout(t); cleanup(); };
  }, [map]);

  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    if (!Array.isArray(parcels) || parcels.length === 0) {
      overlay.setProps({ layers: [] });
      return;
    }
    const data = parcels
      .filter((p) => Array.isArray(p.polygon) && p.polygon.length >= 3)
      .map((p) => ({
        parcelle_id: p.parcelle_id,
        // Deck.gl wants [lng, lat] pairs
        contour: p.polygon.map(([la, lo]) => [lo, la]),
      }));

    const layer = new PolygonLayer({
      id: 'cadastre-parcels',
      data,
      getPolygon: (d) => d.contour,
      // Subtle stroke for the global parcel grid + selected one highlighted
      getFillColor: (d) =>
        d.parcelle_id === selectedParcelleId
          ? [217, 160, 91, 95]
          : [255, 255, 255, 14],
      getLineColor: (d) =>
        d.parcelle_id === selectedParcelleId
          ? [255, 215, 130, 240]
          : [255, 255, 255, 110],
      getLineWidth: (d) => (d.parcelle_id === selectedParcelleId ? 2.5 : 1.2),
      lineWidthMinPixels: 1,
      stroked: true,
      filled: true,
      extruded: false,
      pickable: !!onParcelClick,
      onClick: (info) => {
        if (onParcelClick && info?.object?.parcelle_id) {
          onParcelClick(info.object.parcelle_id);
        }
      },
      updateTriggers: {
        getFillColor: [selectedParcelleId],
        getLineColor: [selectedParcelleId],
        getLineWidth: [selectedParcelleId],
      },
    });
    overlay.setProps({ layers: [layer] });
  }, [parcels, selectedParcelleId, onParcelClick]);

  return null;
};

/* ---------------- Sprint 3.B P2 — Deck.gl plan-source footprints overlay ---------------- */

/** Renders the apartment polygons (cleaned via the Plan Source Editor) as a
 *  gold-filled layer over the cadastre. Click handler navigates to the project. */
const PlanFootprintsOverlay = ({ footprints, selectedProjectId, onFootprintClick }) => {
  const map = useMap();
  const overlayRef = useRef(null);

  useEffect(() => {
    if (!map) return undefined;
    let cancelled = false;
    let cleanup = () => {};
    const attach = () => {
      if (cancelled) return;
      const overlay = new GoogleMapsOverlay({});
      overlay.setMap(map);
      overlayRef.current = overlay;
      cleanup = () => { overlay.setMap(null); overlayRef.current = null; };
    };
    if (window.google?.maps?.event) {
      const listener = window.google.maps.event.addListenerOnce(map, 'idle', attach);
      return () => {
        cancelled = true;
        try { window.google.maps.event.removeListener(listener); } catch {/* noop */}
        cleanup();
      };
    }
    const t = setTimeout(attach, 350);
    return () => { cancelled = true; clearTimeout(t); cleanup(); };
  }, [map]);

  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    if (!Array.isArray(footprints) || footprints.length === 0) {
      overlay.setProps({ layers: [] });
      return;
    }
    const data = footprints
      .filter((f) => Array.isArray(f.polygon_latlon) && f.polygon_latlon.length >= 3)
      .map((f) => ({
        project_id: f.project_id,
        name: f.name,
        surface: f.surface_polygon_m2,
        typology: f.typology,
        // Deck.gl wants [lng, lat]
        contour: f.polygon_latlon.map(([la, lo]) => [lo, la]),
      }));

    const layer = new PolygonLayer({
      id: 'plan-source-footprints',
      data,
      getPolygon: (d) => d.contour,
      getFillColor: (d) =>
        d.project_id === selectedProjectId
          ? [217, 160, 91, 220]
          : [217, 160, 91, 145],
      getLineColor: (d) =>
        d.project_id === selectedProjectId
          ? [255, 220, 150, 255]
          : [217, 160, 91, 220],
      getLineWidth: (d) => (d.project_id === selectedProjectId ? 3 : 2),
      lineWidthMinPixels: 1.5,
      stroked: true,
      filled: true,
      extruded: false,
      pickable: !!onFootprintClick,
      onClick: (info) => {
        if (onFootprintClick && info?.object?.project_id) {
          onFootprintClick(info.object.project_id);
        }
      },
      updateTriggers: {
        getFillColor: [selectedProjectId],
        getLineColor: [selectedProjectId],
        getLineWidth: [selectedProjectId],
      },
    });
    overlay.setProps({ layers: [layer] });
  }, [footprints, selectedProjectId, onFootprintClick]);

  return null;
};

export default VectorMap3D;
